import { useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { UseMutationResult } from '@tanstack/react-query';
import {
  FileUpload,
  fileUploadErrorTranslation,
  type FileUploadError,
} from '../../../shared/components/FileUpload';
import { useToast } from '../../../shared/stores/toast.store';
import { describeApiError } from '../../../shared/api/errors';
import type { EmployeeNotaFiscalDto, EmployeeNotaFiscalListItem } from './types';

export interface NotaFiscalUploadDialogProps {
  open: boolean;
  nota: EmployeeNotaFiscalListItem | null;
  useUploadFile: () => UseMutationResult<EmployeeNotaFiscalDto, Error, { id: number; file: File }>;
  onClose: () => void;
}

export function NotaFiscalUploadDialog({
  open,
  nota,
  useUploadFile,
  onClose,
}: NotaFiscalUploadDialogProps): JSX.Element {
  const { t } = useTranslation();
  const toast = useToast();
  const upload = useUploadFile();
  const [file, setFile] = useState<File | null>(null);
  const [validationError, setValidationError] = useState<FileUploadError | null>(null);

  // Reset ao final da animação de fechamento, não no efeito do `open` (evita
  // setState síncrono no useEffect — regra `react-hooks/set-state-in-effect`).
  function resetState(): void {
    setFile(null);
    setValidationError(null);
  }

  async function handleSubmit(): Promise<void> {
    if (!nota || !file) return;
    try {
      await upload.mutateAsync({ id: nota.id, file });
      toast.success(t('features.colaboradores.notasFiscais.toasts.uploaded'));
      onClose();
    } catch (err) {
      toast.error(describeApiError(err));
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      TransitionProps={{ onExited: resetState }}
    >
      <DialogTitle>{t('features.colaboradores.notasFiscais.upload.title')}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {nota && (
            <Typography variant="body2" color="text.secondary">
              {t('features.colaboradores.notasFiscais.upload.subtitle', {
                periodo: nota.periodo,
                cliente: nota.clienteNome,
              })}
            </Typography>
          )}
          <FileUpload
            value={file}
            onChange={(f, err) => {
              setFile(f);
              setValidationError(err);
            }}
            label={t('features.colaboradores.notasFiscais.upload.selectFile')}
            helperText={t('features.colaboradores.notasFiscais.upload.helper')}
          />
          {validationError && (
            <Alert severity="error">
              {(() => {
                const { key, values } = fileUploadErrorTranslation(validationError);
                return t(key, values);
              })()}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={upload.isPending}>
          {t('common.cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={() => void handleSubmit()}
          disabled={!file || upload.isPending}
        >
          {upload.isPending
            ? t('common.saving')
            : t('features.colaboradores.notasFiscais.upload.submit')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
